import React, { useEffect, useState } from 'react'
import {
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  useTranslate,
  useNotify,
  useEditController,
  required,
  useRedirect,
  maxLength,
} from 'react-admin'
import { Grid, Skeleton } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { NumberInput } from 'src/components/uiElements/fields/NumberInput'
import { productServices } from 'src/services/reservation/product/product.service'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { HasPermission } from 'src/helper/HasPermission'
import { FormFiscalData } from '../../administration/business-unit/logic/FormFiscalData'
import { dueDateServices } from 'src/services/reservation/due-date/dueDate.service'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import { currencyServices } from 'src/services/reservation/currency/currency.service'
import { useErrorHandler } from 'src/hooks/useErrorHandler'
import { countryServices } from 'src/services/reservation/country/country.service'
import { ShowListElement } from 'src/components/uiElements/CRUD/ShowListElement'

export const SupplierEdit = (props) => {
  const notify = useNotify()
  const showError = useErrorHandler()
  const translate = useTranslate()
  const redirect = useRedirect()
  const [productTypes, setProductTypes] = useState([])
  const [loadingProductTypes, setLoadingProductTypes] = useState(false)
  const { record } = useEditController({ resource: 'supplier', id: props.id })
  const [currencyList, setCurrencyList] = useState([])
  const [loadingCurrencyList, setLoadingCurrencyList] = useState(false)
  const [dueDateMethodsMap, setDueDateMethodsMap] = useState([])
  const [loadingDueDateMethodMap, setLoadingDueDateMethodMap] = useState(false)
  const [countryList, setCountryList] = useState([])
  const [loadingCountryList, setLoadingCountryList] = useState(false)

  // const supplierTypes = [{id:1, name:'Aéreo'}, {id:2, name:'Hotel'}, {id:3, name:'Otros prestadores turísticos'}]
  let searchTimeoutCountry

  useEffect(() => {
    getProductTypes()
    getCurrencies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (record?.country_id === record?.country_relation?.id) {
      setCountryList([record?.country_relation])
    }
    if (record?.due_date_method_id && record?.due_date_method_relation) {
      setDueDateMethodsMap([record?.due_date_method_relation])
    }
  }, [record])

  const getProductTypes = async () => {
    try {
      setLoadingProductTypes(true)
      const response = await productServices.getAllProductTypes()
      setProductTypes(response)
      setLoadingProductTypes(false)
    } catch (error) {
      notify(`product types not found`, { type: 'error' })
      setLoadingProductTypes(false)
      return null
    }
  }

  const getCurrencies = async () => {
    try {
      setLoadingCurrencyList(true)
      const allCurrencies = await currencyServices.getByAll()
      setCurrencyList(allCurrencies)
      setLoadingCurrencyList(false)
    } catch (error) {
      setCurrencyList([])
      setLoadingCurrencyList(false)
    }
  }

  const getCountry = async (long_name) => {
    clearTimeout(searchTimeoutCountry)

    searchTimeoutCountry = setTimeout(async () => {
      try {
        setLoadingCountryList(true)
        const allCountries = await countryServices.getByAll({ long_name: long_name })
        setCountryList(allCountries)
        setLoadingCountryList(false)
      } catch (error) {
        setCountryList([])
        setLoadingCountryList(false)
      }
    }, 700)
  }

  const getDueDateMethod = async (name) => {
    if (name?.length < 3) {
      setDueDateMethodsMap([])
      return
    }
    try {
      setLoadingDueDateMethodMap(true)
      const dueDateRange = await dueDateServices.getByAllDueDateMethod({ name__ilike: name })
      setDueDateMethodsMap(dueDateRange)
    } catch (error) {
      setDueDateMethodsMap([])
    } finally {
      setLoadingDueDateMethodMap(false)
    }
  }

  const onSuccess = () => {
    notify('ra.notification.updated_success_masculine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.supplier') },
    })
    redirect('/supplier')
  }

  const onError = (error) => {
    const body = error?.body || error
    if (body?.status_code === 418) {
      const detail = body?.data?.errors || body?.data?.data || body?.data
      error.response = {
        status: 418,
        data: {
          ...body,
          data: { errors: Array.isArray(detail) ? detail : [detail] },
        },
      }
    }
    showError(error)
  }

  const onTransform = (data) =>{
    return {
        ...data,
        due_date_method_id:typeof data.due_date_method_id === 'object' && data.due_date_method_id !== null ? data.due_date_method_id.id : data.due_date_method_id
    }
  }

  return (
    <Edit {...props} transform={onTransform} mutationMode='pessimistic' mutationOptions={{ onSuccess, onError }} actions={null}>
      <CardCommon title={translate('ra.title.update_supplier')} actions={null}>
        <SimpleForm>
          <CardCommon title={translate('ra.title.basic_data')} actions={null} style={{ width: '100%' }}>
            <Grid container spacing={3}>
              <ShowListElement label={translate('ra.field.id')} value={record?.id} sm={12} md={12} />
              <EditInputsWrap label={translate('ra.field.name')} required>
                <TextInput validate={required()} label={false} fullWidth source='name' />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.code')} required>
                <TextInput label={false} fullWidth source='code' validate={[required()]} />
              </EditInputsWrap>
              <EditInputsWrap tooltiptext={translate('ra.message.max_characters', { characters: 100 })} label={translate('ra.field.tma_code')}>
                <TextInput validate={[maxLength(100)]} label={false} fullWidth source='billing_code' />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.currency')} required>
                {loadingCurrencyList ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <SelectInput
                    size='small'
                    optionsText={['short_name']}
                    fullWidth
                    label=''
                    source='currency_id'
                    options={currencyList}
                    validate={[required()]}
                    defaultValue={record?.currency_id}
                  />
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.country')}>
                <AutocompleteInput
                  source='country_id'
                  options={countryList}
                  loading={loadingCountryList}
                  optionsText={['long_name']}
                  optionValue='id'
                  onSearch={getCountry}
                  resetOnLoad={false}
                  useOptionValue={true}
                  positionOptionsText={0}
                  useOptionValueByOptions
                  defaultValue={record?.country_id}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.is_active')}>
                <BooleanInput label={false} fullWidth source='is_active' />
              </EditInputsWrap>
            </Grid>
          </CardCommon>

          <HasPermission permission='fiscal_data' resource={'supplier'}>
            <CardCommon title={translate('ra.title.fiscal_data')} actions={null} style={{ width: '100%', 'margin-top': 32 }}>
              <FormFiscalData isSupplier={true} isEdition={true} />
            </CardCommon>
          </HasPermission>

          <CardCommon title={translate('ra.title.contact')} actions={null} style={{ width: '100%', 'margin-top': 32 }}>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.email')}>
                <TextInput source='email' label='' fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.phone')}>
                <NumberInput source='phone' label='' fullWidth />
              </EditInputsWrap>
            </Grid>
          </CardCommon>

          <CardCommon title={translate('ra.title.configuration')} actions={null} style={{ width: '100%', 'margin-top': 32 }}>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.due_date_method')} required className='small'>
                <AutocompleteInput
                  source={`due_date_method_id`}
                  options={dueDateMethodsMap}
                  loading={loadingDueDateMethodMap}
                  optionsText={['name']}
                  useOptionValue
                  optionValue='id'
                  onSearch={(name) => getDueDateMethod(name)}
                  resetOnLoad={true}
                  fullWidth
                  validate={required()}
                  useOptionValueByOptions
                  defaultValue={record?.due_date_method_id}
                />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.product-types')} required>
                {loadingProductTypes ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='product_type_id'
                    optionsText={['long_name']}
                    optionValue='id'
                    options={productTypes}
                    label=''
                    fullWidth
                    multiple
                    validate={[required()]}
                    defaultValue={record?.product_type_id}
                  />
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.gmx_contract_id')}>
                <NumberInput label={false} fullWidth source='gmx_contract_id' />
              </EditInputsWrap>
              {/* <EditInputsWrap label={translate('ra.field.vat_active')}>
                <BooleanInput label={false} fullWidth source='vat_active' />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.supplier_type')}>
                {loadingProductTypes ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='supplier_type'
                    optionsText={['name']}
                    optionValue='id'
                    options={supplierTypes}
                    label=''
                    fullWidth
                    validate={[required()]}
                  />
                )}
              </EditInputsWrap> */}
            </Grid>
          </CardCommon>
        </SimpleForm>
      </CardCommon>
    </Edit>
  )
}